import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Tab,
  Tabs,
  Paper,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Snackbar,
  InputAdornment,
  IconButton,
  Slider,
  Chip,
  LinearProgress,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import { Save, Send, Visibility, VisibilityOff, CloudUpload, Delete, SmartToy, CheckCircle, Speed, OpenInNew } from '@mui/icons-material';
import { settingsService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';

interface SettingRow {
  chave: string;
  valor: string;
  tipo: string;
  descricao: string;
  grupo: string;
}

const PROVIDER_COLORS: Record<string, string> = {
  openai: '#10a37f',
  google: '#4285f4',
  anthropic: '#d97706',
};

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  google: 'Google',
  anthropic: 'Anthropic',
};

const PROVIDER_KEY_MAP: Record<string, string> = {
  openai: 'ia_openai_api_key',
  google: 'ia_google_api_key',
  anthropic: 'ia_anthropic_api_key',
};

const PROVIDER_MODEL_MAP: Record<string, string> = {
  openai: 'ia_openai_modelo',
  google: 'ia_google_modelo',
  anthropic: 'ia_anthropic_modelo',
};

const COST_PER_MILLION: Record<string, { input: number; output: number }> = {
  openai: { input: 0.15, output: 0.60 },
  google: { input: 0.075, output: 0.30 },
  anthropic: { input: 3.0, output: 15.0 },
};

const SettingsPage: React.FC = () => {
  const { user, hasPermission, municipioAtivo } = useAuth();
  const navigate = useNavigate();
  const { refreshBranding } = useBranding();
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState<SettingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSmtpPass, setShowSmtpPass] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  // IA states
  const [testingIA, setTestingIA] = useState<string | null>(null);
  const [iaUsage, setIaUsage] = useState<any>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [usagePeriod, setUsagePeriod] = useState(7);
  const [showApiKeys, setShowApiKeys] = useState<Record<string, boolean>>({});

  // BI / Superset states
  const [testingBI, setTestingBI] = useState(false);
  const [showBiPass, setShowBiPass] = useState(false);
  const [testingBiDb, setTestingBiDb] = useState(false);
  const [showBiDbPass, setShowBiDbPass] = useState(false);

  // Abre o editor BI embarcado (iframe) na rota interna, mantendo a barra lateral.
  const handleOpenBiEditor = () => {
    if (!municipioAtivo?.id) {
      setSnackbar({ open: true, message: 'Selecione um municipio ativo para abrir o editor BI.', severity: 'error' });
      return;
    }
    navigate('/bi/editor');
  };

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 2) {
      loadIAUsage(usagePeriod);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadSettings = async () => {
    try {
      const data = await settingsService.getAll();
      setSettings(data);
    } catch {
      setSnackbar({ open: true, message: 'Erro ao carregar configuracoes', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getValue = (chave: string) => {
    return settings.find(s => s.chave === chave)?.valor || '';
  };

  const setValue = (chave: string, valor: string) => {
    setSettings(prev =>
      prev.map(s => s.chave === chave ? { ...s, valor } : s)
    );
  };

  const handleSave = async (grupo: string) => {
    setSaving(true);
    try {
      const items = settings
        .filter(s => s.grupo === grupo)
        .map(s => ({ chave: s.chave, valor: s.valor }));
      await settingsService.update(items);
      setSnackbar({ open: true, message: 'Configuracoes salvas com sucesso!', severity: 'success' });
      if (grupo === 'branding') {
        await refreshBranding();
      }
    } catch {
      setSnackbar({ open: true, message: 'Erro ao salvar configuracoes', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!testEmail.trim()) {
      setSnackbar({ open: true, message: 'Informe um email para o teste', severity: 'error' });
      return;
    }
    setTesting(true);
    try {
      const result = await settingsService.testSmtp(testEmail.trim());
      setSnackbar({
        open: true,
        message: result.success ? result.message : `Falha: ${result.message}`,
        severity: result.success ? 'success' : 'error',
      });
    } catch (err: any) {
      setSnackbar({ open: true, message: 'Erro ao testar SMTP', severity: 'error' });
    } finally {
      setTesting(false);
    }
  };

  const handleFileUpload = (chave: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'Arquivo muito grande (max 2MB)', severity: 'error' });
      return;
    }
    if (!file.type.startsWith('image/')) {
      setSnackbar({ open: true, message: 'Selecione um arquivo de imagem', severity: 'error' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setValue(chave, reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // IA functions
  const loadIAUsage = async (dias: number) => {
    setLoadingUsage(true);
    try {
      const data = await settingsService.getIAUsage(dias);
      setIaUsage(data);
    } catch { }
    finally { setLoadingUsage(false); }
  };

  const handleTestIA = async (provider: string) => {
    const apiKey = getValue(PROVIDER_KEY_MAP[provider]);
    const model = getValue(PROVIDER_MODEL_MAP[provider]);
    if (!apiKey) {
      setSnackbar({ open: true, message: 'Informe a API Key antes de testar', severity: 'error' });
      return;
    }
    setTestingIA(provider);
    try {
      // Se a key esta mascarada (apos reload), enviar '__FROM_DB__' para o backend ler do banco
      const keyToSend = apiKey.startsWith('••••') ? '__FROM_DB__' : apiKey;
      const result = await settingsService.testIA(provider, keyToSend, model);
      setSnackbar({
        open: true,
        message: result.success ? `${result.message} (${result.latency_ms}ms)` : result.message,
        severity: result.success ? 'success' : 'error',
      });
    } catch {
      setSnackbar({ open: true, message: 'Erro ao testar provider', severity: 'error' });
    } finally {
      setTestingIA(null);
    }
  };

  const toggleApiKeyVisibility = (provider: string) => {
    setShowApiKeys(prev => ({ ...prev, [provider]: !prev[provider] }));
  };

  const handleTestBI = async () => {
    const url = getValue('bi_superset_url');
    if (!url.trim()) {
      setSnackbar({ open: true, message: 'Informe a URL do Superset', severity: 'error' });
      return;
    }
    setTestingBI(true);
    try {
      const pass = getValue('bi_superset_pass');
      // Se a senha esta mascarada (apos reload), enviar '__FROM_DB__' para o backend ler do banco
      const passToSend = pass.startsWith('••••') ? '__FROM_DB__' : pass;
      const result = await settingsService.testBI(url.trim(), getValue('bi_superset_user'), passToSend);
      setSnackbar({
        open: true,
        message: result.success
          ? `${result.message}${result.latency_ms != null ? ` (${result.latency_ms}ms)` : ''}`
          : result.message,
        severity: result.success ? 'success' : 'error',
      });
    } catch {
      setSnackbar({ open: true, message: 'Erro ao testar conexao com o Superset', severity: 'error' });
    } finally {
      setTestingBI(false);
    }
  };

  const handleTestDatabase = async () => {
    const host = getValue('bi_db_host');
    if (!host.trim()) {
      setSnackbar({ open: true, message: 'Informe o host do banco de dados', severity: 'error' });
      return;
    }
    setTestingBiDb(true);
    try {
      const pass = getValue('bi_db_pass');
      // Se a senha esta mascarada (apos reload), enviar '__FROM_DB__' para o backend ler do banco
      const passToSend = pass.startsWith('••••') ? '__FROM_DB__' : pass;
      const result = await settingsService.testDatabase({
        host: host.trim(),
        port: parseInt(getValue('bi_db_port'), 10) || 5432,
        name: getValue('bi_db_name'),
        user: getValue('bi_db_user'),
        pass: passToSend,
      });
      setSnackbar({
        open: true,
        message: result.success
          ? `${result.message}${result.latency_ms != null ? ` (${result.latency_ms}ms)` : ''}`
          : result.message,
        severity: result.success ? 'success' : 'error',
      });
    } catch {
      setSnackbar({ open: true, message: 'Erro ao testar conexao com o banco de dados', severity: 'error' });
    } finally {
      setTestingBiDb(false);
    }
  };

  const estimateCost = (provider: string, tokensIn: number, tokensOut: number): string => {
    const costs = COST_PER_MILLION[provider];
    if (!costs) return '$0.00';
    const cost = (tokensIn / 1_000_000) * costs.input + (tokensOut / 1_000_000) * costs.output;
    return `$${cost.toFixed(4)}`;
  };

  const formatNumber = (n: number): string => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
  };

  if (!user?.is_admin && !hasPermission('MANAGE_SETTINGS')) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography color="error">Sem permissao para acessar configuracoes.</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Configuracoes
      </Typography>

      <Paper sx={{ borderRadius: '8px' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            borderBottom: '1px solid #D3D4DD',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          }}
        >
          <Tab label="SMTP / Email" />
          <Tab label="Branding" />
          <Tab label="Inteligencia Artificial" />
          <Tab label="BI / Superset" />
        </Tabs>

        {/* Tab SMTP */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontSize: '0.875rem', color: '#696979', mb: 3 }}>
              Configure o servidor SMTP para envio de emails (recuperacao de senha, notificacoes).
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
              <TextField
                label="Servidor SMTP"
                placeholder="smtp.gmail.com"
                value={getValue('smtp_host')}
                onChange={(e) => setValue('smtp_host', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Porta"
                placeholder="587"
                type="number"
                value={getValue('smtp_port')}
                onChange={(e) => setValue('smtp_port', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Usuario SMTP"
                placeholder="usuario@email.com"
                value={getValue('smtp_user')}
                onChange={(e) => setValue('smtp_user', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Senha SMTP"
                type={showSmtpPass ? 'text' : 'password'}
                value={getValue('smtp_pass')}
                onChange={(e) => setValue('smtp_pass', e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowSmtpPass(!showSmtpPass)} edge="end" size="small">
                        {showSmtpPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Nome do remetente"
                placeholder="NeoGestor"
                value={getValue('smtp_from_name')}
                onChange={(e) => setValue('smtp_from_name', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Email do remetente"
                placeholder="noreply@neogestor.com"
                value={getValue('smtp_from_email')}
                onChange={(e) => setValue('smtp_from_email', e.target.value)}
                size="small"
                fullWidth
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={getValue('smtp_secure') === 'true'}
                  onChange={(e) => setValue('smtp_secure', e.target.checked ? 'true' : 'false')}
                />
              }
              label={
                <Typography sx={{ fontSize: '0.875rem' }}>
                  Usar SSL/TLS direto (porta 465)
                </Typography>
              }
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                onClick={() => handleSave('smtp')}
                disabled={saving}
                sx={{ borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }}
              >
                Salvar SMTP
              </Button>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1, minWidth: 300 }}>
                <TextField
                  label="Email para teste"
                  placeholder="teste@email.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={16} /> : <Send />}
                  onClick={handleTestSmtp}
                  disabled={testing}
                  sx={{ borderRadius: '8px', textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  Testar
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Tab Branding */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontSize: '0.875rem', color: '#696979', mb: 3 }}>
              Personalize a aparencia da plataforma (nome, logo, subtitulo).
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
              <TextField
                label="Nome da plataforma"
                placeholder="NeoGestor"
                value={getValue('branding_nome')}
                onChange={(e) => setValue('branding_nome', e.target.value)}
                size="small"
                fullWidth
                helperText="Exibido no login, sidebar e titulo da pagina"
              />
              <TextField
                label="Subtitulo"
                placeholder="Plataforma de Governanca de Indicadores"
                value={getValue('branding_subtitulo')}
                onChange={(e) => setValue('branding_subtitulo', e.target.value)}
                size="small"
                fullWidth
                helperText="Exibido abaixo do nome na tela de login"
              />
            </Box>

            {/* Logo Upload */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#24252E', mb: 1 }}>
                Logo da plataforma
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {getValue('branding_logo') ? (
                  <Box
                    sx={{
                      width: 80, height: 80, borderRadius: '8px',
                      border: '1px solid #D3D4DD', overflow: 'hidden',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: '#F0F0F5',
                    }}
                  >
                    <Box
                      component="img"
                      src={getValue('branding_logo')}
                      alt="Logo"
                      sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 80, height: 80, borderRadius: '8px',
                      border: '2px dashed #D3D4DD',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#AAAAB9', fontSize: '0.75rem',
                    }}
                  >
                    Sem logo
                  </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUpload />}
                    onClick={() => logoInputRef.current?.click()}
                    sx={{ borderRadius: '8px', textTransform: 'none' }}
                  >
                    Enviar logo
                  </Button>
                  {getValue('branding_logo') && (
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => setValue('branding_logo', '')}
                      sx={{ borderRadius: '8px', textTransform: 'none' }}
                    >
                      Remover
                    </Button>
                  )}
                  <Typography sx={{ fontSize: '0.75rem', color: '#AAAAB9' }}>
                    PNG, JPG ou SVG. Max 2MB.
                  </Typography>
                </Box>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleFileUpload('branding_logo')}
                />
              </Box>
            </Box>

            {/* Favicon Upload */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#24252E', mb: 1 }}>
                Favicon
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {getValue('branding_favicon') ? (
                  <Box
                    sx={{
                      width: 40, height: 40, borderRadius: '4px',
                      border: '1px solid #D3D4DD', overflow: 'hidden',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: '#F0F0F5',
                    }}
                  >
                    <Box
                      component="img"
                      src={getValue('branding_favicon')}
                      alt="Favicon"
                      sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 40, height: 40, borderRadius: '4px',
                      border: '2px dashed #D3D4DD',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#AAAAB9', fontSize: '0.625rem',
                    }}
                  >
                    N/A
                  </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUpload />}
                    onClick={() => faviconInputRef.current?.click()}
                    sx={{ borderRadius: '8px', textTransform: 'none' }}
                  >
                    Enviar favicon
                  </Button>
                  {getValue('branding_favicon') && (
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => setValue('branding_favicon', '')}
                      sx={{ borderRadius: '8px', textTransform: 'none' }}
                    >
                      Remover
                    </Button>
                  )}
                  <Typography sx={{ fontSize: '0.75rem', color: '#AAAAB9' }}>
                    ICO, PNG ou SVG. 32x32px recomendado.
                  </Typography>
                </Box>
                <input
                  ref={faviconInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleFileUpload('branding_favicon')}
                />
              </Box>
            </Box>

            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={() => handleSave('branding')}
              disabled={saving}
              sx={{ borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }}
            >
              Salvar Branding
            </Button>
          </Box>
        )}

        {/* Tab Inteligencia Artificial */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            {/* Secao 1 - Provedores */}
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#24252E', mb: 1 }}>
              Provedores de IA
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#696979', mb: 3 }}>
              Configure as chaves de API e modelos para cada provedor de inteligencia artificial.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
              {(['openai', 'google', 'anthropic'] as const).map((provider) => (
                <Card
                  key={provider}
                  variant="outlined"
                  sx={{
                    borderRadius: '8px',
                    borderColor: getValue('ia_provider_padrao') === provider ? PROVIDER_COLORS[provider] : '#D3D4DD',
                    borderWidth: getValue('ia_provider_padrao') === provider ? 2 : 1,
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      borderColor: PROVIDER_COLORS[provider],
                      boxShadow: `0 0 0 1px ${PROVIDER_COLORS[provider]}20`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SmartToy sx={{ color: PROVIDER_COLORS[provider], fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#24252E' }}>
                          {PROVIDER_LABELS[provider]}
                        </Typography>
                      </Box>
                      {getValue('ia_provider_padrao') === provider && (
                        <Chip
                          icon={<CheckCircle sx={{ fontSize: 14 }} />}
                          label="Padrao"
                          size="small"
                          sx={{
                            backgroundColor: `${PROVIDER_COLORS[provider]}15`,
                            color: PROVIDER_COLORS[provider],
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 24,
                            '& .MuiChip-icon': { color: PROVIDER_COLORS[provider] },
                          }}
                        />
                      )}
                    </Box>

                    <TextField
                      label="API Key"
                      type={showApiKeys[provider] ? 'text' : 'password'}
                      value={getValue(PROVIDER_KEY_MAP[provider])}
                      onChange={(e) => setValue(PROVIDER_KEY_MAP[provider], e.target.value)}
                      size="small"
                      fullWidth
                      sx={{ mb: 1.5 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => toggleApiKeyVisibility(provider)} edge="end" size="small">
                              {showApiKeys[provider] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label="Modelo"
                      placeholder={
                        provider === 'openai' ? 'gpt-4o-mini' :
                        provider === 'google' ? 'gemini-2.0-flash' :
                        'claude-sonnet-4-20250514'
                      }
                      value={getValue(PROVIDER_MODEL_MAP[provider])}
                      onChange={(e) => setValue(PROVIDER_MODEL_MAP[provider], e.target.value)}
                      size="small"
                      fullWidth
                      sx={{ mb: 2 }}
                    />

                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      startIcon={
                        testingIA === provider
                          ? <CircularProgress size={14} />
                          : <Speed sx={{ fontSize: 16 }} />
                      }
                      onClick={() => handleTestIA(provider)}
                      disabled={testingIA !== null}
                      sx={{
                        borderRadius: '6px',
                        textTransform: 'none',
                        borderColor: PROVIDER_COLORS[provider],
                        color: PROVIDER_COLORS[provider],
                        '&:hover': {
                          borderColor: PROVIDER_COLORS[provider],
                          backgroundColor: `${PROVIDER_COLORS[provider]}08`,
                        },
                      }}
                    >
                      Testar Conexao
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Seletor de provider padrao */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#24252E', mb: 1 }}>
                Provider padrao
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#696979', mb: 1.5 }}>
                Selecione qual provedor sera utilizado por padrao nas requisicoes de IA.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {(['openai', 'google', 'anthropic'] as const).map((provider) => (
                  <Button
                    key={provider}
                    variant={getValue('ia_provider_padrao') === provider ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setValue('ia_provider_padrao', provider)}
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontWeight: 600,
                      minWidth: 100,
                      ...(getValue('ia_provider_padrao') === provider
                        ? {
                            backgroundColor: PROVIDER_COLORS[provider],
                            '&:hover': { backgroundColor: PROVIDER_COLORS[provider], opacity: 0.9 },
                            boxShadow: 'none',
                          }
                        : {
                            borderColor: '#D3D4DD',
                            color: '#696979',
                            '&:hover': { borderColor: PROVIDER_COLORS[provider], color: PROVIDER_COLORS[provider] },
                          }
                      ),
                    }}
                  >
                    {PROVIDER_LABELS[provider]}
                  </Button>
                ))}
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Secao 2 - Parametros */}
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#24252E', mb: 1 }}>
              Parametros de geracao
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#696979', mb: 2 }}>
              Ajuste os parametros que controlam o comportamento das respostas da IA.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
              <Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#24252E', mb: 1 }}>
                  Temperatura: {getValue('ia_temperatura') || '0.7'}
                </Typography>
                <Slider
                  value={parseFloat(getValue('ia_temperatura') || '0.7')}
                  onChange={(_, v) => setValue('ia_temperatura', String(v))}
                  min={0}
                  max={1}
                  step={0.1}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 0.5, label: '0.5' },
                    { value: 1, label: '1' },
                  ]}
                  sx={{
                    '& .MuiSlider-markLabel': { fontSize: '0.7rem', color: '#AAAAB9' },
                  }}
                />
                <Typography sx={{ fontSize: '0.7rem', color: '#AAAAB9' }}>
                  Valores baixos = mais preciso. Valores altos = mais criativo.
                </Typography>
              </Box>

              <TextField
                label="Max Tokens"
                type="number"
                value={getValue('ia_max_tokens') || '4096'}
                onChange={(e) => setValue('ia_max_tokens', e.target.value)}
                size="small"
                fullWidth
                helperText="Limite maximo de tokens por resposta"
              />

              <TextField
                label="Limite diario de tokens"
                type="number"
                value={getValue('ia_limite_diario_tokens') || '0'}
                onChange={(e) => setValue('ia_limite_diario_tokens', e.target.value)}
                size="small"
                fullWidth
                helperText="0 = ilimitado"
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Secao 3 - Cache */}
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#24252E', mb: 1 }}>
              Cache de respostas
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#696979', mb: 2 }}>
              O cache armazena respostas identicas para evitar chamadas duplicadas a API.
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: 3, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={getValue('ia_cache_habilitado') === 'true'}
                    onChange={(e) => setValue('ia_cache_habilitado', e.target.checked ? 'true' : 'false')}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '0.875rem' }}>
                    Habilitar cache
                  </Typography>
                }
              />
              <TextField
                label="TTL (minutos)"
                type="number"
                value={getValue('ia_cache_ttl_minutos') || '60'}
                onChange={(e) => setValue('ia_cache_ttl_minutos', e.target.value)}
                size="small"
                sx={{ width: 160 }}
                helperText="Tempo de vida do cache"
                disabled={getValue('ia_cache_habilitado') !== 'true'}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Secao 4 - Dashboard de Uso */}
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#24252E', mb: 1 }}>
              Dashboard de uso
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#696979', mb: 2 }}>
              Acompanhe o consumo de tokens, requisicoes e custos estimados por provedor.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {[7, 15, 30].map((dias) => (
                <Button
                  key={dias}
                  variant={usagePeriod === dias ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    setUsagePeriod(dias);
                    loadIAUsage(dias);
                  }}
                  sx={{
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 60,
                    ...(usagePeriod === dias
                      ? { boxShadow: 'none' }
                      : { borderColor: '#D3D4DD', color: '#696979' }
                    ),
                  }}
                >
                  {dias} dias
                </Button>
              ))}
            </Box>

            {loadingUsage && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

            {iaUsage && !loadingUsage && (
              <Box>
                {/* Tabela de uso por provider */}
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    mb: 3,
                    '& th, & td': {
                      px: 2,
                      py: 1.5,
                      textAlign: 'left',
                      fontSize: '0.8rem',
                      borderBottom: '1px solid #E8E8ED',
                    },
                    '& th': {
                      fontWeight: 600,
                      color: '#24252E',
                      backgroundColor: '#F8F8FA',
                    },
                    '& td': {
                      color: '#696979',
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th>Provider</th>
                      <th>Tokens In</th>
                      <th>Tokens Out</th>
                      <th>Requisicoes</th>
                      <th>Cache Hits</th>
                      <th>Custo Estimado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {iaUsage.por_provider && Object.entries(iaUsage.por_provider).map(([provider, data]: [string, any]) => (
                      <tr key={provider}>
                        <td>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: PROVIDER_COLORS[provider] || '#999',
                              }}
                            />
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                              {PROVIDER_LABELS[provider] || provider}
                            </Typography>
                          </Box>
                        </td>
                        <td>{formatNumber(data.tokens_in || 0)}</td>
                        <td>{formatNumber(data.tokens_out || 0)}</td>
                        <td>{formatNumber(data.requisicoes || 0)}</td>
                        <td>{formatNumber(data.cache_hits || 0)}</td>
                        <td>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#24252E' }}>
                            {estimateCost(provider, data.tokens_in || 0, data.tokens_out || 0)}
                          </Typography>
                        </td>
                      </tr>
                    ))}
                    {(!iaUsage.por_provider || Object.keys(iaUsage.por_provider).length === 0) && (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', color: '#AAAAB9' }}>
                          Nenhum uso registrado no periodo
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  {/* Top 5 usuarios */}
                  {iaUsage.top_usuarios && iaUsage.top_usuarios.length > 0 && (
                    <Card variant="outlined" sx={{ borderRadius: '8px', borderColor: '#E8E8ED' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#24252E', mb: 1.5 }}>
                          Top 5 usuarios
                        </Typography>
                        {iaUsage.top_usuarios.slice(0, 5).map((u: any, i: number) => (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 0.75,
                              borderBottom: i < Math.min(iaUsage.top_usuarios.length, 5) - 1 ? '1px solid #F0F0F5' : 'none',
                            }}
                          >
                            <Typography sx={{ fontSize: '0.78rem', color: '#696979' }}>
                              {i + 1}. {u.nome || u.email || 'Usuario'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#24252E' }}>
                              {formatNumber(u.tokens_total || u.requisicoes || 0)} {u.tokens_total ? 'tokens' : 'req'}
                            </Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Info de cache */}
                  {iaUsage.cache && (
                    <Card variant="outlined" sx={{ borderRadius: '8px', borderColor: '#E8E8ED' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#24252E', mb: 1.5 }}>
                          Cache
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '0.78rem', color: '#696979' }}>Entradas ativas</Typography>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#24252E' }}>
                              {iaUsage.cache.entradas_ativas ?? 0}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '0.78rem', color: '#696979' }}>Total de entradas</Typography>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#24252E' }}>
                              {iaUsage.cache.entradas_totais ?? 0}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '0.78rem', color: '#696979' }}>Taxa de hit</Typography>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#24252E' }}>
                              {iaUsage.cache.taxa_hit != null ? `${iaUsage.cache.taxa_hit}%` : 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </Box>
            )}

            {!iaUsage && !loadingUsage && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#AAAAB9' }}>
                  Nenhum dado de uso disponivel. Os dados serao exibidos apos o uso da IA.
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Botao salvar IA */}
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={() => handleSave('ia')}
              disabled={saving}
              sx={{ borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }}
            >
              Salvar Configuracoes IA
            </Button>
          </Box>
        )}

        {/* Tab BI / Superset */}
        {activeTab === 3 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontSize: '0.875rem', color: '#696979', mb: 3 }}>
              Configure o servidor de BI. 
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
              <TextField
                label="URL do Superset"
                placeholder="http://10.0.225.66:8088"
                value={getValue('bi_superset_url')}
                onChange={(e) => setValue('bi_superset_url', e.target.value)}
                size="small"
                fullWidth
                sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}
                helperText="Inclua protocolo e porta. Ex.: http://10.0.225.66:8088"
              />
              <TextField
                label="Usuario de servico"
                placeholder="svc_neogestor"
                value={getValue('bi_superset_user')}
                onChange={(e) => setValue('bi_superset_user', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Senha de servico"
                type={showBiPass ? 'text' : 'password'}
                value={getValue('bi_superset_pass')}
                onChange={(e) => setValue('bi_superset_pass', e.target.value)}
                size="small"
                fullWidth
                helperText="Deixe em branco para manter a senha atual"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowBiPass(!showBiPass)} edge="end" size="small">
                        {showBiPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#24252E', mt: 2, mb: 0.5 }}>
              Banco de Dados (BI)
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#696979', mb: 3 }}>
              Conexao do banco que sera a fonte de dados do BI.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
              <TextField
                label="Host"
                placeholder="10.0.190.201"
                value={getValue('bi_db_host')}
                onChange={(e) => setValue('bi_db_host', e.target.value)}
                size="small"
                fullWidth
                helperText="IP ou hostname do banco"
              />
              <TextField
                label="Porta"
                placeholder="5432"
                type="number"
                value={getValue('bi_db_port')}
                onChange={(e) => setValue('bi_db_port', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Banco de dados"
                placeholder="novaserrana_mg_saude_dev"
                value={getValue('bi_db_name')}
                onChange={(e) => setValue('bi_db_name', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Usuario"
                placeholder="postgres"
                value={getValue('bi_db_user')}
                onChange={(e) => setValue('bi_db_user', e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Senha"
                type={showBiDbPass ? 'text' : 'password'}
                value={getValue('bi_db_pass')}
                onChange={(e) => setValue('bi_db_pass', e.target.value)}
                size="small"
                fullWidth
                sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}
                helperText="Deixe em branco para manter a senha atual"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowBiDbPass(!showBiDbPass)} edge="end" size="small">
                        {showBiDbPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                startIcon={testingBiDb ? <CircularProgress size={16} /> : <Speed />}
                onClick={handleTestDatabase}
                disabled={testingBiDb}
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Testar conexao com BD
              </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                onClick={() => handleSave('bi')}
                disabled={saving}
                sx={{ borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }}
              >
                Salvar BI
              </Button>
              <Button
                variant="outlined"
                startIcon={testingBI ? <CircularProgress size={16} /> : <Speed />}
                onClick={handleTestBI}
                disabled={testingBI}
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Testar conexao
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Editor de paineis (Superset)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Abre o Superset completo numa nova aba, ja autenticado, para criar e editar
                graficos e dashboards. Os dados sao restritos ao municipio ativo
                {municipioAtivo?.nome ? ` (${municipioAtivo.nome})` : ''}.
              </Typography>
              <Button
                variant="contained"
                startIcon={<OpenInNew />}
                onClick={handleOpenBiEditor}
                disabled={!municipioAtivo?.id}
                sx={{ borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }}
              >
                Abrir editor do Superset
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
